// src/components/mobile/KonuAtamaMobileView.tsx
import React, { useState } from 'react';
import { Trash2, AlertTriangle } from 'lucide-react';
import CreatableSelect from 'react-select/creatable';
import { MobileTabNavigation, MobileTab } from './MobileTabNavigation';
import { MobileStickyHeader } from './MobileStickyHeader';
import { MobileStickyActionBar } from './MobileStickyActionBar';
import { MobileCollapsible } from './MobileCollapsible';
import { MobileListCard } from './MobileListCard';
import { AtanmisKonu } from '../../types';
import '../../styles/mobile/mobile-common.css';
import '../../styles/mobile/konu-atama-mobile.css';

interface Department {
  id: number;
  bolum_adi: string;
  firma_adi?: string | null;
  raporlama_yili?: number | null;
}

interface KonuAtamaMobileViewProps {
  // Atanmamış konular
  atanmamisKonular: any[];
  filtreliAtanmamisKonular: any[];
  seciliKonular: Set<number>;
  onKonuCheck: (konuId: number) => void;
  onTumunuSec: () => void;
  onSecimiTemizle: () => void;
  aramaMetni: string;
  onAramaChange: (value: string) => void;
  
  // İşlem paneli
  seciliBolum: { value: number | string; label: string; data?: any } | null;
  onBolumChange: (opt: any) => void;
  onBolumCreate: (inputValue: string) => Promise<void>;
  bolumOptions: any[];
  benzerBolumUyarisi: string | null;
  bolumler: any[];
  selectedFirma: any;
  selectedYil: number;
  bolumIstatistikleri: Record<string, number>;
  onBolumSil: (bolumId: number, bolumAdi: string) => void;
  kullanilmisBolumler: string[];
  
  // Atanmış konular
  atanmisKonular: AtanmisKonu[];
  filtreliAtanmisKonular: AtanmisKonu[];
  secilenBolumFiltresi: string | null;
  onBolumFiltresiChange: (value: string | null) => void;
  benzersizBolumAdlari: string[];
  onUnassignClick: (konu: AtanmisKonu) => void;
  
  // Actions
  isAssigning: boolean;
  onAtamaYap: () => void;
  onPreview: () => void;
}

export const KonuAtamaMobileView: React.FC<KonuAtamaMobileViewProps> = ({
  atanmamisKonular,
  filtreliAtanmamisKonular,
  seciliKonular,
  onKonuCheck,
  onTumunuSec,
  onSecimiTemizle,
  aramaMetni,
  onAramaChange,
  seciliBolum,
  onBolumChange,
  onBolumCreate,
  bolumOptions,
  benzerBolumUyarisi,
  bolumler,
  selectedFirma,
  selectedYil,
  bolumIstatistikleri,
  onBolumSil,
  kullanilmisBolumler,
  atanmisKonular,
  filtreliAtanmisKonular,
  secilenBolumFiltresi,
  onBolumFiltresiChange,
  benzersizBolumAdlari,
  onUnassignClick,
  isAssigning,
  onAtamaYap,
  onPreview
}) => {
  const [activeTab, setActiveTab] = useState<string>('atanmamis');

  const tabs: MobileTab[] = [
    { id: 'atanmamis', label: 'Atanmamış', icon: '📋', count: filtreliAtanmamisKonular.length },
    { id: 'islem', label: 'İşlem', icon: '⚙️' },
    { id: 'atanmis', label: 'Atanmış', icon: '✅', count: filtreliAtanmisKonular.length }
  ];

  const selectStyles = {
    control: (base: any) => ({ ...base, minHeight: '36px', fontSize: '13px' }),
    menu: (base: any) => ({ ...base, fontSize: '13px' }),
    option: (base: any, state: any) => ({
      ...base,
      fontSize: '13px',
      padding: '8px 12px',
      color: '#000000 !important',
      backgroundColor: state.data?.__isNew__
        ? (state.isSelected ? '#10b981' : state.isFocused ? '#d1fae5' : '#ecfdf5')
        : state.data?.isGeneral
        ? (state.isSelected ? '#059669' : state.isFocused ? '#ECFDF5' : '#F0FDF4')
        : (state.isSelected ? '#4CAF50' : state.isFocused ? '#E5F3E6' : 'white')
    }),
    placeholder: (base: any) => ({ ...base, fontSize: '13px' }),
    singleValue: (base: any) => ({ ...base, fontSize: '13px' })
  };

  return (
    <div className="konu-atama-mobile-layout mobile-only">
      <MobileStickyHeader
        title="Konu Atama"
        icon={<span>🔗</span>}
        actions={[
          <button
            key="preview"
            onClick={onPreview}
            disabled={atanmisKonular.length === 0}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: atanmisKonular.length === 0 ? 'not-allowed' : 'pointer',
              opacity: atanmisKonular.length === 0 ? 0.5 : 1,
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              fontSize: '20px'
            }}
            title="Önizle"
          >
            👁️
          </button>
        ]}
      />

      <MobileTabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      <div className="konu-atama-tab-content">
        {/* Tab 1: Atanmamış Konular */}
        {activeTab === 'atanmamis' && (
          <div>
            <div className="konu-atama-search-bar">
              <input
                type="text"
                placeholder="Konu ara..."
                value={aramaMetni}
                onChange={(e) => onAramaChange(e.target.value)}
                className="konu-atama-search-input"
              />
            </div>

            <div className="konu-atama-bulk-actions">
              <button
                onClick={onTumunuSec}
                disabled={filtreliAtanmamisKonular.length === 0}
                className="konu-atama-bulk-btn select-all"
              >
                Tümünü Seç
              </button>
              <button
                onClick={onSecimiTemizle}
                disabled={seciliKonular.size === 0}
                className="konu-atama-bulk-btn clear"
              >
                Temizle
              </button>
            </div>

            {filtreliAtanmamisKonular.length === 0 ? (
              <div className="konu-atama-no-data">
                {aramaMetni ? 'Arama kriterine uygun konu bulunamadı.' : 'Atanacak yeni konu yok.'}
              </div>
            ) : (
              filtreliAtanmamisKonular.map(konu => (
                <div key={`atanmamis-${konu.id}`} className="konu-atama-item">
                  <div className="konu-atama-checkbox-wrapper">
                    <input
                      type="checkbox"
                      id={`mobile-konu-${konu.id}`}
                      checked={seciliKonular.has(konu.id)}
                      onChange={() => onKonuCheck(konu.id)}
                      className="konu-atama-checkbox"
                    />
                    <label htmlFor={`mobile-konu-${konu.id}`} className="konu-atama-checkbox-label" />
                  </div>
                  <label htmlFor={`mobile-konu-${konu.id}`} className="konu-atama-item-content">
                    <span className="konu-atama-item-title">{konu.konu}</span>
                    <span className="konu-atama-item-subtitle">({konu.standart_detay})</span>
                  </label>
                </div>
              ))
            )}
          </div>
        )}

        {/* Tab 2: İşlem Paneli */}
        {activeTab === 'islem' && (
          <div className="konu-atama-islem-panel">
            <label className="konu-atama-label">Sorumlu Bölüm:</label>
            <div className="konu-atama-dropdown">
              <CreatableSelect
                options={bolumOptions}
                value={seciliBolum}
                onChange={onBolumChange}
                onCreateOption={onBolumCreate}
                placeholder="Bölüm seçin veya yeni bölüm adı yazın..."
                isClearable
                createOptionPosition="first"
                formatCreateLabel={(inputValue) => `"➡️ ${inputValue}" bölümünü oluştur (Enter)`}
                noOptionsMessage={() => "Yeni bölüm adı yazıp Enter'a basın"}
                styles={selectStyles}
              />
            </div>

            {benzerBolumUyarisi && (
              <div className="konu-atama-warning">
                <AlertTriangle size={16} className="konu-atama-warning-icon" />
                <span className="konu-atama-warning-text">{benzerBolumUyarisi}</span>
              </div>
            )}

            <MobileCollapsible
              title="Kendi Bölümleriniz"
              count={bolumler.filter(b => b.firma_adi === selectedFirma?.value && b.raporlama_yili === selectedYil).length}
              defaultOpen={false}
            >
              {(() => {
                const firmaBolumleri = bolumler.filter(b => b.firma_adi === selectedFirma?.value && b.raporlama_yili === selectedYil);
                
                if (firmaBolumleri.length === 0) {
                  return (
                    <div className="konu-atama-empty-state">
                      Eğer firmanızın farklı bölümleri varsa, yukarıda Bölüm Adını girerek oluşturabilirsiniz.
                    </div>
                  );
                }
                
                return firmaBolumleri.map(bolum => (
                  <div key={bolum.id} className="konu-atama-bolum-item">
                    <span className="konu-atama-bolum-name">
                      🏢 {bolum.bolum_adi} {bolumIstatistikleri[bolum.bolum_adi] ? `(${bolumIstatistikleri[bolum.bolum_adi]} konu)` : ''}
                    </span>
                    <button
                      onClick={() => onBolumSil(bolum.id, bolum.bolum_adi)}
                      className="konu-atama-bolum-delete"
                      title={`"${bolum.bolum_adi}" bölümünü sil`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ));
              })()}
            </MobileCollapsible>

            <div className="konu-atama-info-box">
              <div className="konu-atama-info-item">
                <span style={{ color: '#059669' }}>●</span>
                <span>Yeşil: Genel bölümler (tüm firmalar kullanabilir)</span>
              </div>
              <div className="konu-atama-info-item">
                <span>🏢</span>
                <span>Firmaya özel bölümler (sadece siz görebilirsiniz)</span>
              </div>
            </div>

            {kullanilmisBolumler.length > 0 && (
              <MobileCollapsible
                title="Kullanılmış Bölümler"
                count={kullanilmisBolumler.length}
                defaultOpen={false}
                maxHeight="120px"
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {kullanilmisBolumler.slice(0, 10).map((bolum, index) => (
                    <span
                      key={index}
                      onClick={() => onBolumChange({ value: `custom_${bolum}`, label: bolum })}
                      style={{
                        fontSize: '11px',
                        backgroundColor: '#E5F3E6',
                        color: '#166534',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        border: '1px solid #BBF7D0'
                      }}
                    >
                      {bolum}
                    </span>
                  ))}
                </div>
              </MobileCollapsible>
            )}
          </div>
        )}

        {/* Tab 3: Atanmış Konular */}
        {activeTab === 'atanmis' && (
          <div>
            <div className="konu-atama-filter">
              <select
                value={secilenBolumFiltresi || ''}
                onChange={(e) => onBolumFiltresiChange(e.target.value || null)}
                className="konu-atama-filter-select"
              >
                <option value="">Tüm Bölümler</option>
                {benzersizBolumAdlari.map(bolum => (
                  <option key={bolum} value={bolum}>
                    {bolum} ({bolumIstatistikleri[bolum]} konu)
                  </option>
                ))}
              </select>
            </div>

            {filtreliAtanmisKonular.length === 0 ? (
              <div className="konu-atama-no-data">
                {secilenBolumFiltresi || aramaMetni ? 'Filtre kriterlerine uygun konu bulunamadı.' : 'Henüz atanmış konu yok.'}
              </div>
            ) : (
              filtreliAtanmisKonular.map((konu) => (
                <div key={`atanmis-${konu.id}`} className="konu-atama-assigned-item">
                  <div className="konu-atama-assigned-header">
                    <button
                      onClick={() => onUnassignClick(konu)}
                      className="konu-atama-delete-btn"
                      title="Atamayı Geri Al"
                    >
                      <Trash2 size={14} />
                    </button>
                    <div style={{ flex: 1 }}>
                      <span className="konu-atama-assigned-title">{konu.konu}</span>
                      <span className="konu-atama-assigned-subtitle">({konu.standart_detay})</span>
                    </div>
                  </div>
                  <div className="konu-atama-assigned-bolum">
                    <strong>{konu.bolum_adi}</strong>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <MobileStickyActionBar
        primaryAction={{
          label: 'Kaydet',
          onClick: onAtamaYap,
          disabled: isAssigning || seciliKonular.size === 0 || !seciliBolum,
          count: seciliKonular.size
        }}
      />
    </div>
  );
};
